/**
 * Client-side search over goals + targets/sub-themes + indicators
 * for the active portal (UN or EU). CP-1: results stay portal-scoped.
 * D-11: EU `targets_eu` posts are sub-themes in product language.
 */

import { decodeHtmlEntities, stripLeadingIndicatorCode } from './displayText';
import { getGoalDisplayTitle } from './goalDisplayTitle';

export type SearchResultKind = 'goal' | 'target' | 'indicator';

export type SearchResult = {
  kind: SearchResultKind;
  id: number;
  label: string;
  secondary?: string;
  goalId?: number | null;
  score: number;
};

function normalize(s: string): string {
  return decodeHtmlEntities(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function scoreMatch(haystack: string, query: string): number {
  const h = normalize(haystack);
  const q = normalize(query);
  if (!q || !h) return 0;
  if (h === q) return 100;
  if (h.startsWith(q)) return 80;
  const idx = h.indexOf(q);
  if (idx === 0) return 75;
  if (idx > 0) return 50 - Math.min(idx, 20);
  const tokens = q.split(' ').filter(Boolean);
  if (tokens.length > 1 && tokens.every((t) => h.includes(t))) return 40;
  return 0;
}

function resolveGoalId(entity: any): number | null {
  if (entity?.goal_id != null) return Number(entity.goal_id) || null;
  if (Array.isArray(entity?.goal_ids) && entity.goal_ids.length) {
    return Number(entity.goal_ids[0]) || null;
  }
  const selectGoal = entity?.acf?.select_goal;
  if (Array.isArray(selectGoal) && selectGoal.length) {
    return Number(selectGoal[0]) || null;
  }
  if (selectGoal != null && selectGoal !== '') {
    return Number(selectGoal) || null;
  }
  return null;
}

export function searchPortalContent(
  query: string,
  goals: any[],
  indicators: any[],
  targets: any[] = [],
  goalType: string = 'un',
  limit = 12
): SearchResult[] {
  const q = (query || '').trim();
  if (q.length < 2) return [];

  const isEu = goalType === 'eu';
  const targetKindLabel = isEu ? 'Sub-theme' : 'Target';

  const goalResults: SearchResult[] = [];
  for (const goal of goals || []) {
    const title = getGoalDisplayTitle(goal) || goal?.title || '';
    const score = Math.max(
      scoreMatch(title, q),
      scoreMatch(String(goal?.acf?.index || goal?.index || ''), q),
      scoreMatch(`goal ${goal?.acf?.index || goal?.index || ''}`, q)
    );
    if (score > 0) {
      goalResults.push({
        kind: 'goal',
        id: goal.id,
        label: title,
        secondary: 'Goal',
        goalId: goal.id,
        score,
      });
    }
  }

  const targetResults: SearchResult[] = [];
  for (const target of targets || []) {
    const code = String(target?.acf?.index || target?.index || '').trim();
    const rawTitle = target?.title_render || target?.title || '';
    const cleanTitle = stripLeadingIndicatorCode(rawTitle, code);
    const score = Math.max(
      scoreMatch(rawTitle, q),
      scoreMatch(cleanTitle, q),
      scoreMatch(code, q),
      scoreMatch(`${isEu ? 'sub-theme' : 'target'} ${code}`, q)
    );
    if (score > 0) {
      targetResults.push({
        kind: 'target',
        id: target.id,
        label: code ? `${code} · ${cleanTitle || rawTitle}` : cleanTitle || rawTitle,
        secondary: targetKindLabel,
        goalId: resolveGoalId(target),
        score,
      });
    }
  }

  const indicatorResults: SearchResult[] = [];
  for (const ind of indicators || []) {
    const code = String(ind?.acf?.index || ind?.indicator_code || '').trim();
    const rawTitle = ind?.title_render || ind?.title || '';
    const cleanTitle = stripLeadingIndicatorCode(rawTitle, code);
    const score = Math.max(
      scoreMatch(rawTitle, q),
      scoreMatch(cleanTitle, q),
      scoreMatch(code, q),
      scoreMatch(`sdg ${code}`, q)
    );
    if (score > 0) {
      indicatorResults.push({
        kind: 'indicator',
        id: ind.id,
        label: code ? `${code} · ${cleanTitle || rawTitle}` : cleanTitle || rawTitle,
        secondary: 'Indicator',
        goalId: ind.goal_id ?? resolveGoalId(ind),
        score,
      });
    }
  }

  goalResults.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  targetResults.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  indicatorResults.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));

  // Goals first, then targets/sub-themes, then indicators — all portal-scoped.
  const goalsTake = goalResults.slice(0, Math.min(3, limit));
  let remaining = Math.max(0, limit - goalsTake.length);
  const targetsTake = targetResults.slice(0, Math.min(4, remaining));
  remaining = Math.max(0, remaining - targetsTake.length);
  const indicatorsTake = indicatorResults.slice(0, remaining);

  return [...goalsTake, ...targetsTake, ...indicatorsTake];
}
